/**
 * PairUpToday Master Permission Management System
 * Enforces Android 14+ / iOS 18+ compliant First-App-Open Permission Flow:
 * Splash -> Explanation -> Camera -> Microphone -> Location -> Notification -> Home/Login
 * 
 * Includes live OS permission detection, graceful fallback explanations for denied items,
 * and persistent local audit storage.
 */

export type PermissionType = 'camera' | 'microphone' | 'location' | 'notification';

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

export interface AppPermissionRecord {
  state: PermissionState;
  updatedAt: string;
  deniedCount: number;
}

export interface AppPermissionsStatus {
  camera: AppPermissionRecord;
  microphone: AppPermissionRecord;
  location: AppPermissionRecord;
  notification: AppPermissionRecord;
  onboardingCompleted: boolean;
  lastChecked: string;
}

const STORAGE_KEY = 'pairup_permissions_status_v1';
const ONBOARDING_COMPLETED_KEY = 'pairup_permission_onboarding_completed';

const DEFAULT_STATUS: AppPermissionsStatus = {
  camera: { state: 'prompt', updatedAt: new Date().toISOString(), deniedCount: 0 },
  microphone: { state: 'prompt', updatedAt: new Date().toISOString(), deniedCount: 0 },
  location: { state: 'prompt', updatedAt: new Date().toISOString(), deniedCount: 0 },
  notification: { state: 'prompt', updatedAt: new Date().toISOString(), deniedCount: 0 },
  onboardingCompleted: false,
  lastChecked: new Date().toISOString(),
};

/**
 * Retrieve cached permission statuses from local storage
 */
export function getStoredPermissions(): AppPermissionsStatus {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const onboarding = localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
    if (!raw) {
      return { ...DEFAULT_STATUS, onboardingCompleted: onboarding };
    }
    const parsed = JSON.parse(raw);
    return {
      camera: parsed.camera || DEFAULT_STATUS.camera,
      microphone: parsed.microphone || DEFAULT_STATUS.microphone,
      location: parsed.location || DEFAULT_STATUS.location,
      notification: parsed.notification || DEFAULT_STATUS.notification,
      onboardingCompleted: onboarding,
      lastChecked: parsed.lastChecked || new Date().toISOString(),
    };
  } catch {
    return DEFAULT_STATUS;
  }
}

/**
 * Save updated permission status
 */
export function saveStoredPermissions(status: Partial<AppPermissionsStatus>): AppPermissionsStatus {
  try {
    const current = getStoredPermissions();
    const updated: AppPermissionsStatus = {
      ...current,
      ...status,
      lastChecked: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (typeof status.onboardingCompleted === 'boolean') {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, status.onboardingCompleted ? 'true' : 'false');
    }
    return updated;
  } catch {
    return DEFAULT_STATUS;
  }
}

/**
 * Check if the user is on their first launch and needs the permission flow
 */
export function isFirstLaunchPermissionNeeded(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) !== 'true';
  } catch {
    return false;
  }
}

/**
 * Mark permission onboarding as completed
 */
export function markPermissionOnboardingCompleted(): void {
  try {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    const curr = getStoredPermissions();
    saveStoredPermissions({ ...curr, onboardingCompleted: true });
  } catch (e) {
    console.error('Failed to mark permission onboarding completed', e);
  }
}

/**
 * Reset permission onboarding (for testing or user request)
 */
export function resetPermissionOnboarding(): void {
  try {
    localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to reset permission onboarding', e);
  }
}

/**
 * Query current live OS/browser permission state without triggering popups
 */
export async function checkLivePermissions(): Promise<AppPermissionsStatus> {
  const current = getStoredPermissions();
  const result: AppPermissionsStatus = { ...current, lastChecked: new Date().toISOString() };

  // 1. Notification (Synchronous API)
  if (typeof window !== 'undefined' && 'Notification' in window) {
    const perm = Notification.permission;
    result.notification.state = perm === 'default' ? 'prompt' : perm;
    result.notification.updatedAt = new Date().toISOString();
  } else {
    result.notification.state = 'unsupported';
  }

  // Helper for navigator.permissions.query
  const queryPermission = async (name: string): Promise<PermissionState> => {
    if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) {
      return 'prompt';
    }
    try {
      const status = await navigator.permissions.query({ name: name as any });
      return status.state as PermissionState;
    } catch {
      // Some browsers (like Safari/Firefox) do not support query({ name: 'camera' })
      return 'prompt';
    }
  };

  // 2. Camera
  try {
    const camState = await queryPermission('camera');
    if (camState !== 'prompt' || result.camera.state === 'prompt') {
      result.camera.state = camState;
      result.camera.updatedAt = new Date().toISOString();
    }
  } catch {
    // leave as stored
  }

  // 3. Microphone
  try {
    const micState = await queryPermission('microphone');
    if (micState !== 'prompt' || result.microphone.state === 'prompt') {
      result.microphone.state = micState;
      result.microphone.updatedAt = new Date().toISOString();
    }
  } catch {
    // leave as stored
  }

  // 4. Location
  try {
    const locState = await queryPermission('geolocation');
    if (locState !== 'prompt' || result.location.state === 'prompt') {
      result.location.state = locState;
      result.location.updatedAt = new Date().toISOString();
    }
  } catch {
    // leave as stored
  }

  saveStoredPermissions(result);
  return result;
}

/**
 * 1️⃣ CAMERA PERMISSION REQUEST
 * Triggers native system camera prompt.
 * If allowed, immediately stops tracks to avoid battery drain / recording indicators.
 */
export async function requestCameraPermission(): Promise<{ state: PermissionState; error?: string }> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { state: 'unsupported', error: 'MediaDevices API is not supported on this device/browser.' };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
    });
    // Immediately stop tracks
    stream.getTracks().forEach((track) => track.stop());

    const current = getStoredPermissions();
    current.camera = { state: 'granted', updatedAt: new Date().toISOString(), deniedCount: 0 };
    saveStoredPermissions(current);
    return { state: 'granted' };
  } catch (err: any) {
    const isDenied = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
    const current = getStoredPermissions();
    const count = (current.camera.deniedCount || 0) + 1;
    current.camera = {
      state: isDenied ? 'denied' : 'prompt',
      updatedAt: new Date().toISOString(),
      deniedCount: count,
    };
    saveStoredPermissions(current);
    return {
      state: isDenied ? 'denied' : 'prompt',
      error: err.message || 'Camera permission was denied or camera is not available.',
    };
  }
}

/**
 * 2️⃣ MICROPHONE PERMISSION REQUEST
 * Triggers native system microphone prompt.
 */
export async function requestMicrophonePermission(): Promise<{ state: PermissionState; error?: string }> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { state: 'unsupported', error: 'MediaDevices API is not supported on this device/browser.' };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    // Immediately stop tracks
    stream.getTracks().forEach((track) => track.stop());

    const current = getStoredPermissions();
    current.microphone = { state: 'granted', updatedAt: new Date().toISOString(), deniedCount: 0 };
    saveStoredPermissions(current);
    return { state: 'granted' };
  } catch (err: any) {
    const isDenied = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
    const current = getStoredPermissions();
    const count = (current.microphone.deniedCount || 0) + 1;
    current.microphone = {
      state: isDenied ? 'denied' : 'prompt',
      updatedAt: new Date().toISOString(),
      deniedCount: count,
    };
    saveStoredPermissions(current);
    return {
      state: isDenied ? 'denied' : 'prompt',
      error: err.message || 'Microphone permission was denied.',
    };
  }
}

/**
 * 3️⃣ LOCATION PERMISSION REQUEST (While Using App / Foreground)
 * Triggers native system location prompt.
 */
export async function requestLocationPermission(): Promise<{
  state: PermissionState;
  coords?: { latitude: number; longitude: number };
  error?: string;
}> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { state: 'unsupported', error: 'Geolocation is not supported by your browser.' };
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        const current = getStoredPermissions();
        current.location = { state: 'granted', updatedAt: new Date().toISOString(), deniedCount: 0 };
        saveStoredPermissions(current);
        resolve({ state: 'granted', coords });
      },
      (err) => {
        const isDenied = err.code === 1; // PERMISSION_DENIED
        const current = getStoredPermissions();
        const count = (current.location.deniedCount || 0) + 1;
        current.location = {
          state: isDenied ? 'denied' : 'prompt',
          updatedAt: new Date().toISOString(),
          deniedCount: count,
        };
        saveStoredPermissions(current);
        resolve({
          state: isDenied ? 'denied' : 'prompt',
          error: err.message || 'Location permission was denied.',
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
}

/**
 * 4️⃣ NOTIFICATIONS PERMISSION REQUEST
 * Triggers native system notification prompt (Android 13+ / iOS Safari / Desktop).
 */
export async function requestNotificationPermission(): Promise<{ state: PermissionState; error?: string }> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { state: 'unsupported', error: 'Notifications are not supported by this browser.' };
  }

  try {
    const result = await Notification.requestPermission();
    const state: PermissionState = result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'prompt';

    const current = getStoredPermissions();
    current.notification = {
      state,
      updatedAt: new Date().toISOString(),
      deniedCount: state === 'denied' ? (current.notification.deniedCount || 0) + 1 : 0,
    };
    saveStoredPermissions(current);

    return { state };
  } catch (err: any) {
    return { state: 'denied', error: err.message || 'Notification permission request failed.' };
  }
}
